export function Privacy() {
  return (
    <div className="editorial-page legal-page">
      {/* ── Hero ─────────────────────────────── */}
      <section className="page-hero grain relative bg-cream-50 pb-8 pt-16 md:pt-20">
        <div className="relative z-10 mx-auto max-w-7xl px-6 text-center">
          <span className="text-sm font-semibold uppercase tracking-widest text-forest-500">
            Legal
          </span>
          <h1 className="mt-4 font-display text-4xl font-semibold tracking-tight text-forest-950 sm:text-5xl">
            Privacy Policy
          </h1>
          <p className="mx-auto mt-4 max-w-lg text-lg text-bark-600">
            Last updated 12 April 2026
          </p>
        </div>
      </section>

      {/* ── Body ─────────────────────────────── */}
      <section className="bg-cream-50 py-16 md:py-24">
        <div className="mx-auto max-w-3xl px-6">
          <div className="prose prose-bark max-w-none space-y-10 text-bark-700 leading-relaxed">

            <div>
              <h2 className="font-display text-xl font-semibold text-forest-950">
                Who we are
              </h2>
              <p className="mt-3">
                Bookburrow is a product of Roguelite Software Ltd, a company registered in England
                and Wales.
              </p>
              <p className="mt-3">
                We are the data controller for the personal data described in this policy. If you
                have any questions, contact us at{" "}
                <a
                  href="mailto:support@bookburrow.uk"
                  className="text-forest-600 underline decoration-forest-300 underline-offset-4 hover:text-forest-800"
                >
                  support@bookburrow.uk
                </a>
                .
              </p>
            </div>

            <div>
              <h2 className="font-display text-xl font-semibold text-forest-950">
                What data we collect
              </h2>
              <p className="mt-3">
                When you use Bookburrow we collect and process the following categories of
                personal data:
              </p>
              <ul className="mt-4 list-disc space-y-2 pl-5">
                <li>
                  <strong>Account data:</strong> name, school name, and email address provided
                  during sign-up.
                </li>
                <li>
                  <strong>Licence and billing data:</strong> licence key, purchase date, and
                  Stripe customer ID. We do not store card numbers &mdash; payment processing
                  is handled entirely by Stripe.
                </li>
                <li>
                  <strong>Crash reports:</strong> when the desktop application encounters an
                  error, it sends a crash report to Sentry containing the error message,
                  stack trace, operating system version, and application version. Crash
                  reports do not include your library catalogue or borrower records.
                </li>
                <li>
                  <strong>Support conversations:</strong> if you contact us via live chat or
                  email, we retain the content of those conversations to provide and improve
                  our support. Live chat is provided by Intercom on our account management
                  website.
                </li>
                <li>
                  <strong>Library data:</strong> book catalogue and borrower records entered
                  by your school. This data is stored locally on your school's machine only
                  and is never transmitted to or stored on our servers.
                </li>
              </ul>
              <p className="mt-4">
                We do not collect, sell, or share your data with advertisers. We do not use
                tracking pixels or behavioural profiling to monitor how you use the desktop
                application. Our account management website uses Intercom for live chat
                support, which may set functional cookies necessary to operate the chat widget.
              </p>
            </div>

            <div>
              <h2 className="font-display text-xl font-semibold text-forest-950">
                How we use your data
              </h2>
              <p className="mt-3">
                We process your personal data on the following legal bases:
              </p>
              <ul className="mt-4 list-disc space-y-2 pl-5">
                <li>
                  <strong>Contract performance:</strong> to create and manage your account,
                  deliver software updates, and provide support.
                </li>
                <li>
                  <strong>Legitimate interests:</strong> to diagnose software errors via
                  crash reports, improve the product, and detect fraud or abuse.
                </li>
                <li>
                  <strong>Legal obligation:</strong> to comply with applicable UK law, including
                  tax and accounting requirements.
                </li>
              </ul>
            </div>

            <div>
              <h2 className="font-display text-xl font-semibold text-forest-950">
                Children's data
              </h2>
              <p className="mt-3">
                Bookburrow is designed to help school libraries manage book lending. Your school
                may enter pupils' names or identifiers as borrower records within the desktop
                application. This data remains on your school's own machine only. We do not
                access, process, or retain pupil data on our servers. Your school is the data
                controller for any pupil data it chooses to enter.
              </p>
              <p className="mt-3">
                Please avoid including pupil names or other pupil personal data in support
                conversations (live chat or email), as these are processed by third-party
                services described below.
              </p>
            </div>

            <div>
              <h2 className="font-display text-xl font-semibold text-forest-950">
                Data retention
              </h2>
              <p className="mt-3">
                We retain your account and licence data for as long as your account is active
                and for up to seven years after closure, to comply with legal and accounting
                obligations. Support conversation history is retained in line with our support
                provider's standard retention policies. You may request deletion of your
                account at any time by emailing us; we will delete personal data within 30
                days, except where retention is required by law.
              </p>
            </div>

            <div>
              <h2 className="font-display text-xl font-semibold text-forest-950">
                Third-party services
              </h2>
              <p className="mt-3">
                We use the following sub-processors:
              </p>
              <ul className="mt-4 list-disc space-y-2 pl-5">
                <li>
                  <strong>Stripe</strong> &mdash; payment processing. See{" "}
                  <a
                    href="https://stripe.com/gb/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-forest-600 underline decoration-forest-300 underline-offset-4 hover:text-forest-800"
                  >
                    stripe.com/gb/privacy
                  </a>
                  .
                </li>
                <li>
                  <strong>Supabase</strong> &mdash; authentication and cloud database hosting
                  for account and licence data. Data is stored in EU data centres. See{" "}
                  <a
                    href="https://supabase.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-forest-600 underline decoration-forest-300 underline-offset-4 hover:text-forest-800"
                  >
                    supabase.com/privacy
                  </a>
                  .
                </li>
                <li>
                  <strong>Cloudflare</strong> &mdash; DNS, CDN, and edge hosting for this
                  website. See{" "}
                  <a
                    href="https://www.cloudflare.com/privacypolicy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-forest-600 underline decoration-forest-300 underline-offset-4 hover:text-forest-800"
                  >
                    cloudflare.com/privacypolicy
                  </a>
                  .
                </li>
                <li>
                  <strong>Sentry</strong> &mdash; crash reporting for the desktop application.
                  See{" "}
                  <a
                    href="https://sentry.io/privacy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-forest-600 underline decoration-forest-300 underline-offset-4 hover:text-forest-800"
                  >
                    sentry.io/privacy
                  </a>
                  .
                </li>
                <li>
                  <strong>Intercom</strong> &mdash; live chat support on our account
                  management website. See{" "}
                  <a
                    href="https://www.intercom.com/legal/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-forest-600 underline decoration-forest-300 underline-offset-4 hover:text-forest-800"
                  >
                    intercom.com/legal/privacy
                  </a>
                  .
                </li>
              </ul>
            </div>

            <div>
              <h2 className="font-display text-xl font-semibold text-forest-950">
                Your rights
              </h2>
              <p className="mt-3">
                Under UK GDPR you have the right to:
              </p>
              <ul className="mt-4 list-disc space-y-2 pl-5">
                <li>Access the personal data we hold about you</li>
                <li>Correct inaccurate data</li>
                <li>Request erasure of your data</li>
                <li>Object to or restrict our processing</li>
                <li>Data portability</li>
                <li>
                  Lodge a complaint with the Information Commissioner's Office (ico.org.uk)
                </li>
              </ul>
              <p className="mt-4">
                To exercise any of these rights, email{" "}
                <a
                  href="mailto:support@bookburrow.uk"
                  className="text-forest-600 underline decoration-forest-300 underline-offset-4 hover:text-forest-800"
                >
                  support@bookburrow.uk
                </a>
                . We will respond within 30 days.
              </p>
            </div>

            <div>
              <h2 className="font-display text-xl font-semibold text-forest-950">
                Changes to this policy
              </h2>
              <p className="mt-3">
                We may update this policy from time to time. We will notify active licence
                holders of material changes by email. Continued use of Bookburrow after
                notification constitutes acceptance of the updated policy.
              </p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
